/**
 * CRIME SCENE — 협동 추리 3D 멀티플레이 기반 시스템 데모.
 * 부트스트랩(로비) + 게임 루프 + 상호작용.
 */

import './style.css';
import { EPISODE_CARDS } from '../../shared/episode-rules';
import { applyPlaceOverrides, fetchPlaceOverrides } from './placeOverrides';
import { Terrain, setTerrain, outdoorHeight } from './terrain';
import { BUILDINGS, ROOMS, OUTDOOR, DOORWAYS, WINDOWS, WALL_H } from '../../shared/level';
import { loadEpisodeData, ep as epData } from './episodeData';
import { buildGround } from './ground';
import * as THREE from 'three';
import { LIGHT_SLOTS } from './budget';
import { REPORT_HZ } from '../../shared/protocol';
import {
  PROPS,
  PROP_BY_ID,
  roundDef,
  LOCKS,
  investigateBlock,
  squadBlock,
} from '../../shared/level';
import { Net, fetchRoomInfo } from './net';
import { bootMark, bootReport } from './boot';
import { buildEpisodeScene } from './episodeScene';
import { MapEditor } from './editor';
import { buildTapes } from './tape';
import { lookOf, buildAvatar } from './avatar';
import { primeAvatarEnv } from './motion';
import { CAST_BY_ID } from '../../shared/cast.gen';
import { setChannelView, setSurfaceResolution, type ChannelView } from './materials';
import { Pipeline, type PipelineView, type Quality } from './pipeline';
import type { PipelineGPU } from './pipelineGPU';
import { createRenderer, IS_GPU, requestShadowUpdate, hideUnported, bundleStatic, drainBuilds } from './gpu';
import { AdaptiveQuality, STEPS, START_PX, ratioFor, type Step } from './adaptive';
import { AIM_LAYER, animateProps, buildProps, highlight, syncProps, type PropView } from './props';
import { LocalPlayer, isTyping } from './player';
import { createDecorPool, keepDecor } from './lightPool';
import { freezeStatic } from './freeze';
import { buildPortals, OUT } from './portals';
import { TouchControls, isTouchDevice } from './touch';
import { RemotePlayers } from './remote';
import { Hud } from './hud';
import { createPhotoRig } from './photo';
import { createRagdollPoser } from './poser';
import { Lobby } from './lobby';
import { Overview } from './overview';
import { buildOutside, horizonColor } from './outside';
import { buildFacades, facadeRoofs } from './architecture';
import { wallBetween } from './world';

const COLORS = ['#8ab4ff', '#ff9f6b', '#79e0a4', '#e08ad6', '#ffd166', '#7fe3e0', '#ff8a8a', '#b8a4ff'];

boot();

function boot() {
  const params = new URLSearchParams(location.search);
  const savedName = localStorage.getItem('cs.name') ?? '';
  const savedColor = localStorage.getItem('cs.color') ?? COLORS[0];
  /** 한 판에 한 코드. 겹칠 일이 없을 만큼만 길면 된다 */
  const newCode = () => {
    const A = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // 헷갈리는 I O 0 1 은 뺀다
    let v = '';
    for (const b of crypto.getRandomValues(new Uint8Array(6))) v += A[b % A.length];
    return v;
  };
  // **URL 의 코드는 남의 방에 참가할 때만 쓴다.** localStorage 에 저장하지 않는다 —
  // 저장하면 다음에 들어와도 끝난 판에 계속 붙는다
  const invited = (params.get('room') ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  // 실제 코드는 입장할 때 정한다 (비었으면 새로 만든다)

  const ui = document.getElementById('ui')!;
  ui.insertAdjacentHTML(
    'beforeend',
    `<div id="lobby"><div class="lobby-card panel">
      <div class="eyebrow">CRIME SCENE</div>
      ${
        invited
          ? `<h1 id="epttl">사건</h1><div class="sub" id="eplead">초대받은 방의 사건으로 들어갑니다.</div>`
          : `<h1 id="epttl">${EPISODE_CARDS[0].title}</h1>
             <div class="sub" id="eplead">${EPISODE_CARDS[0].lead}</div>
             <label>사건</label>
             <div class="eps" id="eps">${EPISODE_CARDS.map(
               (e, i) =>
                 `<button data-ep="${e.id}" aria-pressed="${i === 0}">
                    <b>${e.title}</b><span>${e.meta}</span>
                    ${e.ready ? '' : '<i>준비 중</i>'}
                  </button>`,
             ).join('')}</div>`
      }
      <label for="nm">이름</label>
      <input type="text" id="nm" maxlength="16" placeholder="탐정 이름" value="${escapeAttr(savedName)}" />
      <label>색상</label>
      <div class="colors" id="cols"></div>
      <label for="rm">${
        invited ? '초대받은 방' : '방 코드 — 비워 두면 새 방이 열립니다'
      }</label>
      <input type="text" id="rm" maxlength="12" placeholder="친구 방 코드가 있으면 여기에"
             autocomplete="off" spellcheck="false" value="${escapeAttr(invited)}" />
      <button class="primary" id="go">${invited ? '이 방에 들어가기' : '새 방 만들기'}</button>
      <a class="catlink" href="/catalog">가구 카탈로그 보기 →</a>
      <div class="hint">${
        isTouchDevice()
          ? `<b>왼쪽</b>을 끌면 이동 (끝까지 밀면 달리기) · <b>오른쪽</b>을 끌면 시야<br>
             화면을 톡 치거나 <b>조사</b> 버튼으로 상호작용<br>
             오른쪽 아래 버튼: 손전등 · 가리키기 · 증거 보드 · 채팅`
          : `<kbd>W A S D</kbd> 이동 · <kbd>Shift</kbd> 달리기 · <kbd>C</kbd> 앉기 · <kbd>E</kbd> 상호작용<br>
             <kbd>T</kbd> 손전등 · <kbd>Q</kbd> 가리키기 · <kbd>B</kbd> 증거 보드 · <kbd>G</kbd> 내려놓기<br>
             <kbd>Enter</kbd> 채팅 · <kbd>V</kbd> 시점 전환 · <kbd>Esc</kbd> 마우스 해제<br>
             조사하러 나갈 때 <kbd>P</kbd> 같이 갈 사람 정하기 · <kbd>F8</kbd> 혼자 나가기`
      }</div>
    </div></div>`,
  );

  let color = savedColor;
  const cols = document.getElementById('cols')!;
  cols.innerHTML = COLORS.map(
    (c) => `<button data-c="${c}" style="background:${c}" aria-pressed="${c === color}"></button>`,
  ).join('');
  cols.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-c]');
    if (!b) return;
    color = b.dataset.c!;
    for (const n of cols.querySelectorAll('button')) n.setAttribute('aria-pressed', String(n === b));
  });

  /* 고른 사건. 초대로 들어온 경우엔 **보내지 않는다** — 방이 이미 정해 뒀고,
     여기서 다른 것을 실어 보내도 서버가 무시한다(그게 맞다) */
  let episode = EPISODE_CARDS[0].id as string;
  const eps = document.getElementById('eps');
  eps?.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-ep]');
    if (!b) return;
    episode = b.dataset.ep!;
    for (const n of eps.querySelectorAll('button')) n.setAttribute('aria-pressed', String(n === b));
    const card = EPISODE_CARDS.find((x) => x.id === episode)!;
    document.getElementById('epttl')!.textContent = card.title;
    document.getElementById('eplead')!.textContent = card.note ? `${card.lead} (${card.note})` : card.lead;
  });

  const nameInput = document.getElementById('nm') as HTMLInputElement;
  const roomInput = document.getElementById('rm') as HTMLInputElement;
  const start = () => {
    const name = (nameInput.value.trim() || '탐정') .slice(0, 16);
    // 비워 두면 새 방. 지난 코드를 물려주지 않는다
    const code = (roomInput.value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '') || newCode()).slice(0, 12);
    localStorage.setItem('cs.name', name);
    localStorage.setItem('cs.color', color);
    localStorage.removeItem('cs.room');
    // 기존 쿼리(q, render 등)를 지우지 말 것 — 지우면 화질/렌더 옵션이 통째로 날아간다
    // **주소창에 코드를 남기지 않는다.** 남기면 새로고침할 때마다 끝난 판으로 돌아간다.
    // 부를 때는 대기실의 「초대 링크 복사」를 쓴다
    const next = new URLSearchParams(location.search);
    next.delete('room');
    const q = next.toString();
    history.replaceState(null, '', q ? `?${q}` : location.pathname);
    document.getElementById('lobby')!.remove();
    void run(code, name, color, invited ? undefined : episode);
  };
  document.getElementById('go')!.addEventListener('click', start);
  for (const el of [nameInput, roomInput]) {
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') start();
    });
  }
  if (!savedName) nameInput.focus();
}

/**
 * 방에서 도는 사건을 알아낸다.
 *
 * 새 방이면 로비에서 고른 것이 답이다. **초대 링크로 들어온 경우엔 모른다** —
 * 방이 이미 정해 뒀기 때문에 물어봐야 한다. 씬을 짓기 전에 알아야 해서
 * 웹소켓을 기다리지 않고 HTTP 로 먼저 묻는다.
 *
 * 못 물어보면(네트워크 실패, 없는 방) 로비가 고른 것 또는 기본값으로 간다 —
 * 여기서 막으면 방이 안 열린다.
 */
async function episodeOf(room: string, picked?: string): Promise<string> {
  if (picked) return picked;
  // 방 서버(도쿄 먼저, Cloudflare 예비)에 묻는다 — 판이 있는 서버가 답한다(net.ts)
  const j = await fetchRoomInfo(room);
  if (j?.exists && j.episode) return j.episode;
  return EPISODE_CARDS[0].id;
}

async function run(room: string, name: string, color: string, episode?: string) {
  bootMark('시작');
  const ep = await episodeOf(room, episode);
  bootMark('사건 묻기');
  // **씬을 짓기 전에** 이 사건의 물건·방 목록을 올린다. props/hud/overview 가 그것을 읽는다
  const data = await loadEpisodeData(ep);
  bootMark('사건 데이터');
  // 배치 툴(place.html)이 옮겨 둔 자리를 씬 짓기 전에 얹는다 — 솔버 위의 오버라이드 층
  const placeOv = await fetchPlaceOverrides(data.id);
  applyPlaceOverrides(placeOv);
  bootMark('배치 오버라이드');
  // 절차 재질 생성은 수백 ms 동안 메인 스레드를 잡는다 — 먼저 화면에 알리고 한 프레임 양보한다
  const loading = document.createElement('div');
  loading.id = 'loading';
  loading.innerHTML = '<div>마을을 짓는 중…</div>';   // (옛 저택 데모 문구가 남아 있었다)
  document.getElementById('ui')!.appendChild(loading);
  // rAF 로 양보하면 안 된다 — 백그라운드 탭에서는 rAF 가 멈춰 게임이 아예 시작되지 않는다
  await new Promise((r) => setTimeout(r, 40));

  // 화질: URL ?q= 가 우선, 없으면 저장값. low 는 포스트프로세싱을 끄고 텍스처도 작게 굽는다.
  const qs = new URLSearchParams(location.search);
  // ?render=0 : 게임 루프는 돌되 화면을 그리지 않는다. 동기화 자동 검증에서
  // 여러 클라이언트를 한 머신에 띄울 때 GPU/CPU 경합을 없애기 위한 모드.
  const noRender = qs.get('render') === '0';
  const urlQ = qs.get('q') as Quality | null;
  // 재컴파일 스톨을 유발하는 디버그 키(F3)는 로컬이나 ?debug=1 에서만 받는다
  const debugKeys =
    qs.get('debug') === '1' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
  const touch = isTouchDevice();
  if (touch) document.body.classList.add('touch');
  // 모바일 GPU 는 포스트프로세싱 픽셀 비용을 감당하기 어렵다 — 처음부터 medium 에서 시작하고
  // 나머지는 적응형이 판단한다 (high 로 시작해 버벅인 뒤 내려가는 것보다 첫인상이 낫다)
  const quality: Quality =
    urlQ && ['high', 'medium', 'low'].includes(urlQ)
      ? urlQ
      : ((localStorage.getItem('cs.quality') as Quality | null) ?? (touch ? 'medium' : 'high'));
  setSurfaceResolution(quality === 'low' ? 160 : quality === 'medium' ? 256 : 384);

  // 렌더 해상도 상한. 모바일은 DPR 이 3 을 넘기도 해서 따로 묶는다.
  const maxRatio = touch ? 1.5 : 2;
  // 시작 단계 — high 라도 4K 네이티브에서 출발하지 않는다. 화면이 아무리 커도
  // 1080p 상당에서 시작하고, 여유가 확인되면 적응형이 올린다.
  const startStep = Math.max(
    0,
    STEPS.findIndex((st) => st.quality === quality && st.budgetPx <= START_PX),
  );

  // --- 렌더러 ---
  const canvas = document.getElementById('view') as HTMLCanvasElement;
  // `?gpu=1` + https 면 WebGPU(gpu.ts). 아니면 지금까지의 WebGL
  const renderer = await createRenderer(canvas);
  bootMark('렌더러');
  // 포스트 패스마다 자동 리셋되면 프레임 전체의 draw call 을 볼 수 없다
  renderer.info.autoReset = false;
  // 렌더 픽셀 수는 **DPR 상한이 아니라 예산**으로 정한다 (adaptive.ts:ratioFor).
  // DPR 로 묶으면 4K + 배율 100% 를 놓친다 — 그 경우 DPR 이 1 이라 어떤 상한에도
  // 걸리지 않는데 창이 3840x2160 이라 1080p 의 네 배를 그린다.
  renderer.setPixelRatio(ratioFor(STEPS[startStep].budgetPx, maxRatio));
  renderer.setSize(window.innerWidth, window.innerHeight);
  // 저사양: 그림자를 끄면 셰이더 변형 수가 크게 줄어 초기화가 빨라진다
  renderer.shadowMap.enabled = quality !== 'low';
  // 0.184 에서 PCFSoftShadowMap 이 폐기됐다. 실제로 쓰는 타입을 직접 지정해야
  // 그림자 샘플러 불일치로 조명 드로우콜이 통째로 죽는 사고를 피할 수 있다.
  renderer.shadowMap.type = THREE.PCFShadowMap;
  // 그림자는 **자동 갱신하지 않는다.** 캐스터의 압도적 다수가 병합된 정적 덩어리
  // (StaticBatcher, matrixAutoUpdate=false)인데 autoUpdate 는 그걸 매 프레임 다시
  // 굽는다. 게다가 그 덩어리들은 frustumCulled=false 라 반경 11m 짜리 스팟 그림자
  // 절두체에도 마을 전체가 제출된다 — 실측으로 그림자 라이트 하나가 켜지는 데
  // draw call 480개(788 -> 1268)가 붙었다. 아래 루프에서 움직임이 있을 때만,
  // 그것도 초당 SHADOW_HZ 회까지만 다시 굽는다.
  renderer.shadowMap.autoUpdate = false;
  // 아바타 재질에 쓸 환경맵을 미리 굽는다. **씬 전체에 걸지 않는다** — 밤의 폐교라
  // 벽·가구까지 환경광을 받으면 어둠이 날아간다(motion.ts 머리말 참고)
  void primeAvatarEnv(renderer);
  renderer.shadowMap.needsUpdate = true;
  /** 다음 기회에 그림자를 다시 구워야 하는가 */
  let shadowDirty = true;
  let shadowAcc = 0;
  /** 그림자 재굽기 상한. 60Hz 로 굽던 것을 여기까지만 허용한다 */
  const SHADOW_DT = 1 / 20;
  // 톤매핑은 파이프라인의 Final 패스가 소유한다 (pipeline.ts) — 여기서 켜면 두 번 먹는다

  const scene = new THREE.Scene();
  // 안개 색은 하늘 돔의 지평선 색과 **같아야** 한다. 다르면 멀리 있는 능선이
  // 하늘과 다른 색으로 뭉개져서 판때기처럼 보인다 (대기 원근이 깨진다).
  // 배경은 outside.ts 의 하늘 돔이 덮으므로 여기서는 안 칠한다.
  scene.background = horizonColor;
  scene.fog = new THREE.FogExp2(horizonColor.getHex(), 0.0125);

  const camera = new THREE.PerspectiveCamera(74, window.innerWidth / window.innerHeight, 0.12, 260);

  const pipeline: Pipeline | PipelineGPU = IS_GPU
    ? new (await import('./pipelineGPU')).PipelineGPU(renderer, scene, camera, quality)
    : new Pipeline(renderer, scene, camera, quality);
  bootMark('파이프라인');



  /** 내가 쓴 10자 요약. 서버에도 보내지만 화면에 다시 그리려면 여기도 둔다 */
  const memos: Record<string, string> = {};

  // 어느 집을 지을지는 **이 방의 사건**이 정한다 (episodeScene.ts)
  const built = buildEpisodeScene(scene, renderer, ep, IS_GPU);
  bootMark('세계(buildWorld)');
  // 출입통제선 — 사건 현장(한서진의 집)에만. 막는 게 아니라 표시하는 것이다.
  // 점집에는 없다: 굿 보러 온 사람들이 발견한 밤이고 경찰이 아직 안 왔다
  if (data.tapes) buildTapes(scene);
  // 발밑 바닥 — 기단·툇마루·계단을 걸어서 오르내리게 하고, **프롭이 앉을 높이도
  // 여기서 나온다.** 그래서 프롭보다 먼저 만들어야 한다
  /* **높이맵 지형.** 판·경사로가 주던 높이를 시작값으로 1m 격자에 담고, 에디터가 붓으로 바꾼 변화량을 얹는다.
   * 건물 발판 셀은 잠긴다. 발밑(ground)과 땅 메시(outside)가 이 격자를 읽는다 */
  const baseGround = buildGround(data.level);
  const terrain = new Terrain(data.level, (x, z) => outdoorHeight(OUTDOOR, x, z, baseGround.topAt(x, z) ?? 0), [...BUILDINGS, ...ROOMS]);
  terrain.applyDeltas(placeOv.terrain);
  setTerrain(terrain);
  const ground = buildGround(data.level, terrain);
  bootMark('지형·바닥');
  const props = buildProps(built, renderer, (x, z) => ground.at(x, z, 99) ?? 0);
  bootMark('소품');
  /* **멀어지면 저절로 닫힌다** (2026-09-10, Chris: 「캐릭터 멀어지면 자동으로 닫히고」).
   * 열어 둔 뚜껑·서랍이 그대로 남으면 다음 사람이 「누가 열었나」를 오해한다.
   * 손 닿는 거리의 두 배를 0.9초 넘게 벗어나면 닫기를 보낸다 — 상태의 주인은 서버라 act 로 부탁한다. */
  const autoShut = new Map<string, number>();
  const closeFarLids = () => {
    const now = performance.now();
    for (const v of props.values()) {
      const k = v.def.kind;
      if (k !== 'lid' && k !== 'door') continue;
      if (!net.obj(v.def.id).open) { autoShut.delete(v.def.id); continue; }
      // 문은 지나가고 나면 닫힌다(3.2m). 덮개는 손 닿는 거리의 두 배
      const away = k === 'door' ? 3.2 : (v.def.reach ?? 2.4) * 2;
      const far = Math.hypot(v.def.pos[0] - player.pos.x, v.def.pos[2] - player.pos.z) > away;
      if (!far) { autoShut.delete(v.def.id); continue; }
      const t0 = autoShut.get(v.def.id);
      if (t0 === undefined) { autoShut.set(v.def.id, now); continue; }
      // **'shut' 으로 보낸다** — 'use' 는 사거리 검사에 걸려 거절되고, 그 재시도가 행동 예산을 태워
      // 다른 조작까지 막았다(2026-09-10). 'shut' 은 닫기 전용이라 거리·예산을 안 쓴다
      if (now - t0 > (k === 'door' ? 1600 : 900)) { autoShut.delete(v.def.id); net.send({ t: 'act', id: v.def.id, a: 'shut' }); }
    }
  };
  /**
   * **현장 사진.** 단서를 처음 조사하면 그 물건을 내가 서 있던 자리에서 엔진으로 한 장 찍는다.
   * 수첩의 폴라로이드가 이 사진이다 — 카탈로그 그림이 아니라 「내가 이걸 이렇게 봤다」.
   */
  const photos = createPhotoRig(
    renderer,
    scene,
    () => decor.slots[decor.slots.length - 1],   // 플래시는 장식 풀 슬롯을 빌린다 — 조명 개수를 안 바꾼다
    () => ('composer' in pipeline ? pipeline.composer?.renderTarget1 : null),   // 본 화면과 같은 형식으로 찍는다(photo.ts)
  );
  // 포즈 저장소 — 시신 자세는 여기 있는 `corpse` 가 기본값을 이긴다. 실패해도 게임은 뜬다
  try {
    const r = await fetch('/api/poses', { cache: 'no-store' });
    if (r.ok) {
      const poses = (await r.json()) as Record<string, import('./motion').Pose>;
      (epData() as { poses?: Record<string, import('./motion').Pose> }).poses = poses;
      // 시신은 이미 세워진 뒤다(buildProps 가 먼저 돈다) — 받은 자세를 바로 얹는다.
      // 모델이 아직 안 왔어도 setPose 는 스타일에 남겨 두므로 도착하면 적용된다
      const a = props.get('body')?.group.userData.avatar as { setPose: (p: unknown) => void } | undefined;
      if (poses.corpse && a) a.setPose(poses.corpse);
    }
  } catch (e) { console.warn('[포즈] 저장소를 못 읽었다 — 코드 기본값으로', e); }
  bootMark('포즈');
  let ragdollPoser: ReturnType<typeof createRagdollPoser> | null = null;
  const hitMeshes: THREE.Object3D[] = [];
  for (const v of props.values()) hitMeshes.push(...v.hit);

  // --- 네트워크 ---
  const net = new Net(room, name, color, episode);
  const player = new LocalPlayer(camera, canvas, built.colliders, scene, ground);
  const overview = new Overview();
  const outside = buildOutside(scene, data.village);
  bootMark('바깥(지형 메시·숲)');
  let cullPieceAcc = 0;
  let decorAcc = 0;
let snowOff = false;
  /** 지금 켜 둔 층. 바뀔 때만 전환한다 */
  let shownFloor = '';
  // 건물 외피. 벽(매스)은 world.ts 가, 밖에서 보이는 껍데기는 여기가 소유한다.
  // **사흘 전용이다** — `BUILDINGS`(사흘 마을 건물 목록)를 순회하므로
  // 다른 사건에서 지으면 남의 동네 건물이 통째로 선다
  if (data.village) {
    const facades = buildFacades(scene);
    console.info('[외피]', facades);
  }
  bootMark('외피');
  /* 가로등·현관등(27개)은 가까운 4개만 켠다 — 픽셀마다 모든 조명을 돌아서 맥북에서도 11fps 였다(lightPool.ts).
   * 외피(현관등)까지 다 지은 뒤, 셰이더를 굽기 전에 만들어야 슬롯 수가 처음부터 고정된다 */
  // WebGPU 는 클러스터 조명이라 가로등·현관등을 다 켜 둔다(gpu.ts)
  const decor = IS_GPU ? keepDecor(scene) : createDecorPool(scene, LIGHT_SLOTS.decor);
  if (IS_GPU) console.info('[gpu] 못 옮긴 셰이더 재질 숨김', hideUnported(scene));
  /** WebGPU: 움직이지 않는 세계를 칸마다 Render Bundle 로(gpu.ts). 프레임마다 시야 칸만 켠다 */
  /**
   * **소품을 방마다 상자에 담는다**(portals.ts) — 안 보이는 방은 상자째 끈다. 소품 자신의 visible 은 게임(덮개·집기)이 쓰므로
   * 건드리지 않는다. 세계 루트에 바로 붙은 소품만(가구에 얹힌 것은 가구를 따라 꺼진다)
   */
  const roomIdSet = new Set(ROOMS.map((r) => r.id));
  /** 방마다 하나씩 미리 만든다 — 물건을 빈 방에 내려놓아도 새 상자가 안 생기게(새 상자는 포털 적용 전까지 늘 보인다) */
  const roomBoxes = new Map<string, THREE.Group>();
  for (const r of [...roomIdSet, OUT]) { const box = new THREE.Group(); box.name = `room:${r}`; built.root.add(box); roomBoxes.set(r, box); }
  for (const v of props.values()) {
    if (v.group.parent !== built.root) continue;
    roomBoxes.get(roomIdSet.has(v.def.room) ? v.def.room : OUT)!.add(v.group);
  }
  /**
   * **내려놓은 물건은 내려놓은 방의 상자로 옮긴다.** 원래 방 상자에 두면 원래 방이 안 보이는 자리에서 물건이 사라진다 —
   * 남이 옮겨 온 물건도 같다. 서버가 준 자리(p)가 없으면 제자리(def.room)
   */
  const reboxDropped = () => {
    for (const v of props.values()) {
      if (v.def.kind !== 'pickup' || !v.group.parent?.name.startsWith('room:')) continue;
      const p = net.obj(v.def.id).p;
      const r = p ? epData().roomAt(p[0], p[2])?.id : v.def.room;
      const box = roomBoxes.get(r && roomIdSet.has(r) ? r : OUT)!;
      if (v.group.parent !== box) box.add(v.group);
    }
  };
  // 상자 안의 조명(시신 곁 등)은 상자 밖으로 — 상자를 끄면 조명도 꺼져 조명 개수가 바뀌고 전 재질이 다시 컴파일된다(budget.ts).
  // 월드 자리는 그대로 둔다(attach)
  const boxedLights: THREE.Light[] = [];
  for (const box of roomBoxes.values()) box.traverse((o) => { if ((o as THREE.Light).isLight) boxedLights.push(o as THREE.Light); });
  for (const l of boxedLights) built.root.attach(l);
  /**
   * 움직이지 않는 물체의 행렬을 얼린다(freeze.ts). 매 프레임 움직이는 노드 — 소품의 문짝·서랍·뚜껑(moving),
   * 조각 뚜껑의 경첩(openParts), 미닫이 두 짝 — 는 뺀다. `?freeze=0` 이면 끈다(비교용)
   */
  const animatedNodes = new Set<THREE.Object3D>();
  for (const v of props.values()) {
    if (v.moving) { animatedNodes.add(v.moving); for (const k of ['leaf1', 'leaf2']) { const l = v.moving.userData[k] as THREE.Object3D | undefined; if (l) animatedNodes.add(l); } }
    for (const p of v.openParts ?? []) animatedNodes.add(p.obj);
  }
  // 세계 루트와 마을(외피·나무·지형) 둘 다. 마을 쪽 하늘 돔은 카메라를 따라가는데 — 스스로 풀린다(freeze.ts)
  const freezes = qs.get('freeze') === '0' ? [] : scene.children.filter((c) => (c as THREE.Group).isGroup).map((c) => freezeStatic(c, (o) => animatedNodes.has(o)));
  // 씬 자체도 — 씬이 매 프레임 제 행렬을 만들면 아래 전부에 「다시 계산」이 강제된다(freeze.ts)
  if (freezes.length) scene.matrixAutoUpdate = false;
  bootMark('그 밖(포털·조명풀·UI·얼리기)');
  const frozen = freezes.length ? { update: () => { for (const f of freezes) f.update(); }, thaw: () => { for (const f of freezes) f.thaw(); }, stats: () => freezes.map((f) => f.stats()) } : null;
  if (frozen) console.info('[얼림]', frozen.stats());
  const bundles = IS_GPU ? bundleStatic(built.root, renderer) : null;
  if (bundles) console.info('[gpu] 정적 세계 번들', bundles.moved, '개 ·', bundles.tiles, '칸');
  decor.update(player.pos);
  const remotes = new RemotePlayers(scene, net);
  const moon = scene.getObjectByName('moon') as THREE.DirectionalLight | undefined;
  const moonCell = { x: NaN, z: NaN };
  /**
   * 눈 위에 발자국을 남긴다. 나와 남이 같은 문으로 들어온다.
   *
   * **방 판정은 여기서 한다.** 실내 바닥과 마당은 높이가 같아서(둘 다 `baseY`)
   * outside 쪽에서는 못 가른다 — 방을 아는 건 에피소드 데이터뿐이다.
   */
  const footstep = (id: string, x: number, y: number, z: number, yaw: number) =>
    outside.footstep(id, x, y, z, yaw, epData().roomAt(x, z) != null);
  remotes.onStep = footstep;

  // 대기실과 숙지. 단계가 lobby/read 인 동안 3D 위에 덮인다
  const lobby = new Lobby(net);

  const hud = new Hud(document.getElementById('ui')!, net, {
    onChat: (m) => net.send({ t: 'chat', m }),
    onCode: (id, code) => net.send({ t: 'unlock', id, code }),
    onPair: (id) => net.send({ t: 'pair', with: id }),
    onMemo: (clueId, text) => {
      memos[clueId] = text;
      net.send({ t: 'memo', clueId, text });
    },
    onShot: (clueId) => net.send({ t: 'shot', clueId }),
    onBodyPart: (part) => net.send({ t: 'bodypart', part }),
    onSpeaker: (id) => net.send({ t: 'speaker', id }),
    onPin: (clueId) => net.send({ t: 'pin', clueId }),
    onFlag: (cardId) => net.send({ t: 'flag', cardId }),
    onBallot: (id) => net.send({ t: 'ballot', id }),
    onChoose: (id, opt) => net.send({ t: 'act', id, a: 'choose', arg: opt }),
    onStage: (v) => net.send({ t: 'stage', v }),
    onDrop: (id) => net.send({ t: 'act', id, a: 'drop' }),
    onModal: (open) => {
      if (open) player.unlock();
    },
  });
  hud.photoOf = (id) => photos.get(id);
  hud.memoOf = (id) => memos[id] ?? '';

  // 사양을 모르는 PC 에서 렉이 나면 유저가 설정을 뒤지기 전에 알아서 내린다
  const applyStep = (step: Step) => {
    renderer.setPixelRatio(ratioFor(step.budgetPx, maxRatio));
    renderer.setSize(window.innerWidth, window.innerHeight);
    if (step.quality !== pipeline.quality) {
      renderer.shadowMap.enabled = step.quality !== 'low';
      pipeline.setQuality(step.quality);
    }
    pipeline.setSize(renderer.domElement.width, renderer.domElement.height);
    // 해상도가 바뀌면 그림자 맵도 다시 굽는다
    shadowDirty = true;
  };
  const adaptive = new AdaptiveQuality(startStep, applyStep, (step, dir) => {
    hud.feed(`${dir === 'down' ? '화질을 낮췄습니다' : '화질을 올렸습니다'} — ${step.label}`, 'sys');
  });
  // URL 로 화질을 못 박았거나 유저가 직접 고르면 자동 조절은 물러난다
  if (urlQ) adaptive.disable();

  player.touchMode = touch;
  const touchUI = touch
    ? new TouchControls(
        document.getElementById('ui')!,
        player,
        {
          interact: () => interact(),
          board: () => hud.openBoard(),
          mark: () => mark(),
          chat: () => hud.openChat(),
          drop: () => {
            const mine = net.myItems();
            if (mine.length) net.send({ t: 'act', id: mine[0], a: 'drop' });
          },
          toggleLight: () => player.toggleLight(),
        },
        () => hud.modalOpen || hud.chatOpen,
      )
    : null;

  net.on('ready', () => {
    hud.show();
    hud.toast('화면을 클릭하면 조작이 시작됩니다');
    // 서버가 배정한 스폰으로 맞춘다 (재접속 시 서로 겹치지 않도록)
    if (net.spawn) {
      player.pos.set(net.spawn[0], 0, net.spawn[1]);
      player.yaw = net.spawn[2];
    }
    syncProps(props, net.world, built, net.you);
    reboxDropped();
  });

  net.on('bodypart', (r) => hud.bodyResult(r));
  net.on('world', () => {
    syncProps(props, net.world, built, net.you);
    reboxDropped();
    // 내가 발견한 단서는 바로 펼쳐 보여준다
    const latest = Object.values(net.world.clues).sort((a, b) => b.at - a.at)[0];
    if (latest && latest.by === net.you && latest.at > lastClueShown) {
      lastClueShown = latest.at;
      const bodyClue = epData().bodyParts.some((p) => p.clue === latest.id);
      const pv = [...props.values()].find((v) => v.def.clue === latest.id) ?? (bodyClue ? props.get('body') : undefined);
      if (pv) photos.shoot(latest.id, pv.group, pv.group.position, camera.position);
      // 시신 부위 단서는 부위 화면 안에서 보여 준다 — 카드로 덮지 않는다
      if (!bodyClue) hud.openClue(latest, memos[latest.id] ?? '');
      else if (latest.id === 'c_body') hud.openBody([...epData().bodyParts]);
    }
  });
  let lastClueShown = 0;

  net.connect();

  // --- 셰이더 프리컴파일 -----------------------------------------------------
  //
  // three 는 재질이 **처음 화면에 들어올 때** 셰이더를 컴파일한다. 그래서 물건이 많은
  // 방향을 처음 돌아보면 그 프레임에서 수십 개가 한꺼번에 컴파일되고 화면이 멎는다.
  // 로딩이 끝나기 전에 씬 전체를 한 번 컴파일해 두면 그 스톨이 사라진다.
  //
  // `compileAsync` 는 프로그램을 나눠 컴파일하고 Promise 를 준다(three r152+). 동기
  // `compile()` 은 여기서 통째로 멎으므로 로딩 화면을 붙잡고 있어야 한다 — 어차피 로딩
  // 중이니 그래도 되지만, async 쪽이 브라우저에 숨 쉴 틈을 준다.
  //
  // 활성 조명 개수는 **조명 풀이 못 박아 둔다**(스포트 12 · 포인트 10 슬롯이 늘 켜져 있고
  // 내용만 갈린다 — world.ts 참고). 그래서 여기서 구운 프로그램이 게임 내내 그대로 쓰이고,
  // 방에 들어갈 때 새로 컴파일할 조합이 생기지 않는다.
  loading.innerHTML = '<div>셰이더를 굽는 중…</div>';
  try {
    await renderer.compileAsync(scene, camera);
  } catch {
    // 구형 브라우저에 compileAsync 가 없으면 동기 컴파일로 대신한다
    renderer.compile(scene, camera);
  }

  // **compile 만으로는 부족하다.** 그것은 메인 패스의 재질만 굽고 **그림자 패스의 depth
  // 변종은 굽지 않는다.** 그래서 예열을 해 두고도 Wayne 이 「처음 맵에 진입해서 뒤돌아보는
  // 순간 엄청나게 렉 걸리고 화면이 멈춘다」고 했다 — 재 보니 한 프레임이 8.7초였고 회전
  // 도중 프로그램이 21개 늘었다.
  //
  // 실제 렌더 경로를 한 번 통과시켜야 한다. 두 가지를 임시로 젖힌다:
  //   - 프러스텀 컬링: 켜져 있으면 카메라에 안 든 물건은 그려지지 않아 안 구워진 채 남는다.
  //     끄면 한 프레임에 씬 전체가 메인 패스와 그림자 패스를 모두 지난다.
  //   - shadowMap.autoUpdate: 이 게임은 false 로 두고 필요할 때만 needsUpdate 를 세운다.
  //     안 세우면 그림자 패스가 돌지 않아 정작 굽고 싶은 depth 변종이 안 나온다.
  //
  // **한 번으로는 부족하다.** 로딩 시점에 씬에 없는 것이 있다(로비를 지나 조사가 시작될 때
  // 붙는 것들). 로드 때 예열해도 회전 중 21개가 그대로 구워졌던 것이 그 증거다. 그래서
  // 함수로 두고 조사 단계에 처음 들어갈 때 한 번 더 부른다.
  const warmScene = async () => {
    const restore: THREE.Object3D[] = [];
    scene.traverse((o) => {
      if (o.frustumCulled) {
        restore.push(o);
        o.frustumCulled = false;
      }
    });
    for (let i = 0; i < 2; i++) {
      requestShadowUpdate(renderer, scene);
      try {
        pipeline.render(1 / 60);
      } catch {
        // 예열은 실패해도 게임이 굴러가야 한다 — 스톨이 남을 뿐이다
      }
      await new Promise((r) => requestAnimationFrame(r));
    }
    // WebGPU: 셰이더 대기열이 빌 때까지 더 그린다(gpu.ts drainBuilds). WebGL 은 곧장 끝난다
    await drainBuilds(renderer, () => { requestShadowUpdate(renderer, scene); try { pipeline.render(1 / 60); } catch { /* 위와 같다 */ } },
      (s) => { if (loading.isConnected) loading.innerHTML = `<div>셰이더를 굽는 중… ${s.toFixed(0)}초</div>`; });
    for (const o of restore) o.frustumCulled = true;
  };
  await warmScene();

  bootMark('셰이더 굽기');
  {
    const b = bootReport();
    console.info('[boot] 단계별 소요(ms)', b, '전체', Object.values(b).reduce((a, v) => a + v, 0));
  }
  loading.remove();

  // 자동 QA(tools/mp-check.mjs)와 디버깅에서 게임 내부를 관측/조작하는 훅.
  // 서버 권위 모델이라 여기서 뭘 호출해도 서버 검증을 거친다.
  const dbg = {
    /** 부팅 단계별 소요(ms) — boot.ts */
    boot: bootReport,
    THREE,   // 도구가 Raycaster 같은 것을 만들 수 있게 (tools/*.mjs)
    net,
    player,
    props,
    renderer,
    scene,
    camera,
    fps: 0,
    frames: 0,
    lastDeny: '',
    act: (id: string, a = 'use', arg?: string) => net.send({ t: 'act', id, a, arg }),
    /**
     * 검증 도구용 순간이동.
     *
     * `player.pos` 만 바꾸면 서버는 옛 자리를 안 채로 있어서 「너무 멉니다」가
     * 나온다. 화면과 서버를 같이 옮긴다. 이게 없어서 맵을 눈으로 확인할 수가
     * 없었다 — 이 서버엔 GPU 가 없어 12초 걸어야 2.5m 를 간다
     */
    tp: (x: number, z: number, yaw = 0, pitch = 0) => {
      player.pos.x = x;
      player.pos.z = z;
      player.yaw = yaw;
      player.pitch = pitch;
      net.send({ t: 'move', x, y: player.pos.y, z, yaw, pitch, flags: 0 });
    },
    hud,
    buildAvatar,   // 도구가 배역별 아바타를 세워 볼 수 있게
    /** 조준 중인 것에 E 를 누른 것과 같다 (잠금 키패드 등 UI 경로까지 탄다) */
    interact: () => interact(),
    /**
     * 검증용: 지금 조준된 프롭 id / 조준 재계산.
     * aimed 는 rAF 안의 updateAim 이 세팅하는데, 소프트웨어 렌더에서는 프레임이
     * 불규칙이라 눈이 자세를 못 따라간다 — 검증이 updateAim 을 직접 부르면
     * 「E 를 눌렀을 때 무엇이 일어나는가」를 그 자리에서 확정할 수 있다
     */
    getAimed: () => aimed?.def.id ?? null,
    updateAim: () => updateAim(),
    remoteCount: () => [...net.players.keys()].filter((id) => id !== net.you && net.buffers.has(id)).length,
    pipeline,
    adaptive,
    /** 행렬 얼리기 현황(freeze.ts) */
    frozen: () => frozen?.stats() ?? null,
    /** 현장 사진(photo.ts) — 도구가 찍는 시간을 잰다 */
    photos,
    /** 포털 컬링 켜고 끄기(portals.ts) — 같은 탭에서 켬/끔을 번갈아 재려고 */
    setPortal: (on: boolean) => { portalOn = on; portalSig = ''; },
    visibleRooms: () => portalSig,
    ground,
    setView: (v: PipelineView) => pipeline.setView(v),
    setQuality: (q: Quality) => {
      pipeline.setQuality(q);
      localStorage.setItem('cs.quality', q);
    },
  };
  (window as unknown as { __cs: typeof dbg }).__cs = dbg;
  // 내 대본. 서버가 나에게만 보낸다 — 다른 사람 것은 클라이언트에 아예 안 온다
  let myScript: {
    castId: string;
    culprit: boolean;
    sections: { title: string; body: string }[];
  } | null = null;
  net.on('script', (s) => {
    myScript = s;
    hud.openCast(s);
  });
  net.on('world', () => {
    if (!myScript && net.world.roles[net.you ?? '']) net.send({ t: 'script' });
  });

  function myClues() {
    return Object.values(net.world.clues).filter((c) => c.by === net.you).sort((a, b) => a.at - b.at);
  }
  function showBrief() {
    hud.openBrief(myClues(), memos);
  }
  // 조사가 아닌 단계로 넘어가면 그 화면을 연다
  let lastPhase = net.world.phase;
  // 조사에 처음 들어갈 때 예열을 한 번 더 돈다 — 로딩 때 씬에 없던 것이 이때 붙는다.
  // 대본 창이 아직 화면을 덮고 있는 순간이라 사람은 이 프레임을 보지 못한다
  let warmedPlay = false;
  net.on('world', () => {
    if (net.world.phase !== lastPhase) {
      lastPhase = net.world.phase;
      if (lastPhase !== 'investigate') showBrief();
    }
    if (!warmedPlay && net.world.phase === 'investigate') {
      warmedPlay = true;
      void warmScene();
    }
  });
  net.on('verdict', (v) => hud.openVerdict(v));

  net.on('note', (msg) => { if (msg) hud.feed(msg, 'sys'); });   // 빈 서랍·빈 옷장 — 뒤졌다는 감각
  // 선택형 조사 — 서버가 exam 완료를 알리면 선택지를 연다
  net.on('choose', (id) => {
    const v = props.get(id);
    if (v?.def.choose) hud.openChoose(id, v.def.name, v.def.choose.prompt, [...v.def.choose.options]);
  });
  /* [R] 재생 (v8 A11) — 누군가 기계를 틀면 방 전원에게 온다.
   * street_17 은 음원이 아니라 영상(mp4) — 나머지는 m4a */
  const PLAY_VIDEO = new Set(['street_17']);
  net.on('play', (id) => {
    const v = props.get(id);
    if (!v?.def.play) return;
    const ext = PLAY_VIDEO.has(v.def.play) ? 'mp4' : 'm4a';
    hud.playMedia(`/audio/sahol/${v.def.play}.${ext}`, v.def.name);
  });
  net.on('deny', (why) => {
    dbg.lastDeny = why;
    // **띄운다.** 여기서 안 띄우면 E 를 눌러도 아무 일도 안 일어난 것처럼 보인다
    if (why) hud.feed(why, 'sys');
  });

  // --- 인게임 맵 에디터 (F7) ---
  // 조각을 집어 옮기고 좌표를 TS 코드로 내보낸다. 레벨의 소스는 코드이므로 씬을 저장하지
  // 않는다 — 자세한 이유는 editor.ts 머리말에 있다.
  const editor = new MapEditor(built.root, camera, (m) => hud.feed(m, 'sys'));
  // 디버그 훅에 얹는다 — 자동 검증이 키 입력 없이 에디터를 직접 두드릴 수 있어야 한다
  (window as unknown as { __cs: { editor?: MapEditor } }).__cs.editor = editor;
  // 집기·놓기는 왼쪽 클릭이다. **캔버스가 아니라 window 에 단다** — 캔버스에 달았더니
  // 클릭이 한 번도 에디터에 오지 않았다(HUD 오버레이와 포인터 락 요청이 먼저 먹는다).
  // 대신 버튼·입력 위에서 누른 것은 UI 조작이므로 흘려보낸다.
  const uiHit = (t: EventTarget | null) =>
    t instanceof Element && !!t.closest('button, input, textarea, select, a, .panel, #lobby');
  window.addEventListener('mousedown', (e) => {
    if (e.button !== 0 || uiHit(e.target)) return;
    if (editor.click()) e.preventDefault();
  });
  // 휠은 집은 물체의 거리 — 편집 모드가 아니면 원래 동작에 맡긴다
  window.addEventListener(
    'wheel',
    (e) => {
      if (uiHit(e.target)) return;
      if (editor.wheel(e.deltaY)) e.preventDefault();
    },
    { passive: false },
  );

  // --- 입력 ---
  let aimed: PropView | null = null;
  const ray = new THREE.Raycaster();
  ray.far = 6;
  // 조준은 전용 레이어만 본다 (히트박스는 렌더 레이어에 없다)
  ray.layers.set(AIM_LAYER);
  const worldRay = new THREE.Raycaster();

  const CHANNELS: ChannelView[] = ['off', 'albedo', 'normal', 'roughness', 'ao'];
  let channelIdx = 0;

  /**
   * **Ctrl+W 로 판이 날아가는 것을 막는다.**
   *
   * W 는 앞으로 걷는 키다. 달리려고 Shift 를 짚다가 Ctrl 을 스치면 탭이 닫히고
   * 90분짜리 판이 통째로 사라진다 — 실제로 겪는 사고다.
   *
   * 브라우저는 Ctrl+W 자체를 **막게 해 주지 않는다**(사용자가 창을 닫을 권리다).
   * 대신 `beforeunload` 로 「정말 나가겠냐」를 띄운다. 판에 들어와 있을 때만
   * 건다 — 로비에서까지 물으면 성가시다.
   */
  const guard = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };
  window.addEventListener('beforeunload', guard);

  window.addEventListener('keydown', (e) => {
    // 편집 모드는 **백틱(`)** 이다. F 키는 이미 다 쓰인다(F4 F6 F7 F8 F9) — 처음에 F7 로
    // 넣었다가 「일찍 돌아가기」를 덮어버렸다. 게리모드의 콘솔 키와도 같아 손이 기억한다.
    if (e.code === 'Backquote' && !isTyping()) {
      frozen?.thaw();   // 편집기는 조각을 옮긴다 — 얼린 행렬을 푼다
      editor.toggle();
      e.preventDefault();
      return;
    }
    // **편집 모드에서는 에디터가 먼저 먹는다.** E(조사)·G·Q·Z·X 가 게임 조작과 겹치므로
    // 여기서 가로채지 않으면 조각을 돌리려다 서랍을 연다.
    if (!isTyping() && editor.key(e.code, e.shiftKey, e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      return;
    }
    if (e.code === 'F4') {
      const order: PipelineView[] = ['final', 'ao', 'bloom', 'raw'];
      const next = order[(order.indexOf(pipeline.getView()) + 1) % order.length];
      pipeline.setView(next);
      hud.feed(`파이프라인 뷰: ${next}`, 'sys');
      e.preventDefault();
      return;
    }
    if (e.code === 'F6') {
      const order: Quality[] = ['high', 'medium', 'low'];
      const next = order[(order.indexOf(pipeline.quality) + 1) % order.length];
      renderer.shadowMap.enabled = next !== 'low';
      pipeline.setQuality(next);
      pipeline.setSize(renderer.domElement.width, renderer.domElement.height);
      localStorage.setItem('cs.quality', next);
      adaptive.disable();
      hud.feed(`화질: ${next} (자동 조절 해제)`, 'sys');
      e.preventDefault();
      return;
    }
    // 호스트가 라운드를 넘긴다. 라운드마다 열리는 구역과 조사 횟수가 달라진다
    if (e.code === 'KeyB' && !isTyping()) {
      showBrief();
      return;
    }
    if (e.code === 'KeyC' && !isTyping()) {
      if (myScript) hud.openCast(myScript);
      else {
        net.send({ t: 'script' });          // 없으면 받아와서 연다
        hud.openCast(null);
      }
      return;
    }
    if (e.code === 'F10' && !isTyping()) {
      if (net.isHost()) net.send({ t: 'roles' });
      else hud.toast('호스트가 배역을 나눕니다.');
      return;
    }
    if (e.code === 'KeyO' && !isTyping()) {
      hud.openSquads();
      return;
    }
    /**
     * **P = 촬영.** 단서 카드를 보고 있으면 그것을, 아니면 조준한 물건(이미 조사한 것)을 찍는다.
     * 크라임씬 동작 그대로 — E 로 보고, P 로 찍고, 찍은 것만 나중에 칠판에 건다.
     */
    if (e.code === 'KeyP' && !isTyping()) {
      if (hud.shootOpen()) return;
      const cid = aimed?.def.clue;
      if (cid && net.world.clues[cid]) {
        if (net.world.shots?.[cid]) hud.toast('이미 찍어 둔 것입니다.');
        else { net.send({ t: 'shot', clueId: cid }); hud.flash(); }
      } else hud.toast('먼저 E 로 조사한 뒤 P 로 찍습니다.');
      return;
    }
    if (e.code === 'F8' && !isTyping()) {
      net.send({ t: 'turn', v: 'go' });   // 혼자 나가기 / 전원 조사 시작
      return;
    }
    if (e.code === 'F7' && !isTyping()) {
      net.send({ t: 'turn', v: 'return' }); // 일찍 돌아가기
      return;
    }
    // 새 판 — 월드는 저장되기 때문에 어제 열어 둔 문이 오늘도 열려 있다
    if (e.code === 'F9' && !isTyping()) {
      if (net.isHost()) net.send({ t: 'reset' });
      else hud.toast('호스트만 새 판을 시작할 수 있습니다.');
      return;
    }
    if (e.code === 'F2' && !isTyping()) {
      const next = (net.world.round % 3) + 1;
      net.send({ t: 'round', v: next });
      e.preventDefault();
      return;
    }
    if (e.code === 'KeyM' && !isTyping()) {
      const on = overview.toggle();
      hud.feed(on ? '조감도 — M 을 다시 누르면 돌아간다' : '조감도 해제', 'sys');
      lightKey = '';
      e.preventDefault();
      return;
    }
    // F3 은 재질의 map/normalMap 을 갈아끼우고 **씬의 모든 라이트를 숨긴다.**
    // three 는 텍스처 define 과 라이트 개수를 프로그램 캐시 키에 넣으므로 씬 전체가
    // 재컴파일되어 초 단위로 멈춘다 (shrineSet.ts:239 에 프로그램 28->60, 프레임
    // p95 5.4초 로 같은 사고가 기록돼 있다). 개발용이니 개발 환경에서만 받는다.
    if (e.code === 'F3') {
      if (!debugKeys) return;
      channelIdx = (channelIdx + 1) % CHANNELS.length;
      setChannelView(CHANNELS[channelIdx], scene);
      hud.feed(`재질 채널: ${CHANNELS[channelIdx]}`, 'sys');
      e.preventDefault();
      return;
    }
    if (e.code === 'Escape') {
      if (hud.modalOpen) {
        hud.closeModal();
        e.preventDefault();
      }
      return;
    }
    if (hud.modalOpen) {
      if (/^Digit[0-9]$/.test(e.code)) hud.key(e.code.slice(5));
      else if (e.code === 'Enter') hud.key('ok');
      else if (e.code === 'Backspace') hud.key('clear');
      return;
    }
    if (isTyping()) return;

    switch (e.code) {
      case 'KeyE':
        interact();
        break;
      case 'KeyB':
        hud.openBoard();
        break;
      case 'KeyF':
        // **파기 전용 키.** 삽을 들고 파기 자리를 보고 있을 때만 — 다른 조준
        // 상호작용은 E 가 그대로 담당한다 (2026-09-18, Chris: 「F 키로 파고」)
        if (aimed?.def.dig) net.send({ t: 'act', id: aimed.def.id, a: 'use' });
        break;
      case 'KeyR':
        /* **[R] 음원/영상 재생** (v8 A11) — 조사가 끝난 기계에서만. 조사 여부의
         * 판정은 서버가 다시 한다 — 클라는 프롬프트와 같은 기준으로 미리 걸러
         * 헛누름에 deny 가 안 뜨게 한다 */
        if (aimed?.def.play && playReady(aimed.def)) {
          net.send({ t: 'act', id: aimed.def.id, a: 'play' });
        }
        break;
      case 'KeyG': {
        const mine = net.myItems();
        if (mine.length) net.send({ t: 'act', id: mine[0], a: 'drop' });
        break;
      }
      case 'KeyQ':
        mark();
        break;
      case 'Enter':
        hud.openChat();
        e.preventDefault();
        break;
    }
  });

  function aimPoint(): THREE.Vector3 | null {
    worldRay.setFromCamera(new THREE.Vector2(0, 0), camera);
    worldRay.far = 30;
    const hits = worldRay.intersectObjects(built.root.children, true);
    for (const h of hits) {
      if (h.object.userData.propId) continue;
      return h.point;
    }
    return null;
  }

  function mark() {
    const p = aimPoint();
    if (!p) return;
    net.send({ t: 'mark', x: round(p.x), y: round(p.y), z: round(p.z) });
  }

  function interact() {
    // 아무것도 안 보고 E — 들고 있는 것을 발밑에 내려놓는다
    // (2026-09-18, Chris: 「E 로 집고 E 로 바닥에 두고」)
    if (!aimed) {
      const mine = net.myItems();
      if (mine.length) net.send({ t: 'act', id: mine[0], a: 'drop' });
      return;
    }
    const def = aimed.def;
    const st = net.obj(def.id);

    if (def.kind === 'board') return hud.openBoard();

    // 잠긴 것 — 먼저 열어야 조사할 수 있다. 여는 시도는 조사 횟수를 안 쓴다
    // 조사 규칙에 막히면 **누르기 전에** 이유를 보여준다. 서버와 같은 함수를 쓴다
    const why = blockWhy(def);
    if (why) return hud.feed(`${def.name} — ${why}`, 'sys');

    const lock = LOCKS[def.id];
    if (lock && !net.world.unlocked[def.id]) {
      if (lock.kind === 'code') return hud.openKeypad(def.id, def.name, lock.hint, lock.digits);
      return net.send({ t: 'unlock', id: def.id });
    }
    if (def.kind === 'safe' && st.solved) {
      const c = net.world.clues[def.clue ?? ''];
      if (c) return hud.openClue(c, memos[c.id] ?? '');
    }
    // 시신은 두 번째부터 **부위 조사 화면**이다 (Chris 09-08: 크라임씬처럼 부위별로)
    if (def.id === 'body' && def.clue && net.world.clues[def.clue]) {
      return hud.openBody([...epData().bodyParts]);
    }
    if (def.kind === 'exam' && def.clue && net.world.clues[def.clue]) {
      return hud.openClue(net.world.clues[def.clue], memos[def.clue] ?? '');
    }
    /* 선택형 조사 — exam 을 마친(solved) 기계는 누르면 선택지가 바로 열린다.
     * 아직이면 act('use') 로 떨어지고, 서버가 solved 를 켜며 choose 신호를 보낸다 */
    if (def.choose && def.clue && !net.world.clues[def.clue] && net.obj(def.id).solved) {
      return hud.openChoose(def.id, def.name, def.choose.prompt, [...def.choose.options]);
    }
    net.send({ t: 'act', id: def.id, a: 'use' });
  }

  function updateAim() {
    ray.setFromCamera(new THREE.Vector2(0, 0), camera);
    const hits = ray.intersectObjects(hitMeshes, false);
    let found: PropView | null = null;
    for (const h of hits) {
      const id = h.object.userData.propId as string | undefined;
      if (!id) continue;
      const v = props.get(id);
      if (!v) continue;
      const def = v.def;
      const st = net.obj(def.id);
      // 서랍 속 미공개 아이템 등 숨겨진 대상은 조준되지 않는다.
      // **hiddenIn 검사가 pickup 에만 걸려 있었다** — 그래서 닫힌 서랍 속의
      // exam 단서가 서랍보다 먼저 잡혔고, 서랍을 열 수가 없었다
      if (def.kind === 'pickup' && st.taken) continue;
      if (def.hiddenIn) {
        const cover = net.obj(def.hiddenIn);
        if (!(cover.open || cover.taken)) continue;   // 열리거나 치워져야 조준된다
      }
      // **파기 자리는 삽을 들어야 비로소 존재한다.** 프롬프트가 뜨는 순간
      // 「여기 판다」가 들통나므로, 도구 없이는 주변 땅과 똑같이 취급한다
      // (2026-09-18, Chris: 「삽을 들고 있으면 파기가 활성화」)
      if (def.dig && !net.myItems().includes(def.needs ?? '')) continue;
      // **꺼진 불 아래는 조준 자체가 안 된다** (사흘 v8 A2 — 암실 트레이는 세이프라이트를
      // 켜야 보인다). 어두울 때 프롬프트가 뜨면 「거기 뭔가 있다」를 공짜로 알게 된다.
      // 서버 gate 도 같은 이유로 한 번 더 막는다
      if (def.needsSwitch && net.obj(def.needsSwitch).on !== true) continue;
      // **내려놓은 물건은 자리가 옮겨진다.** def.pos 는 「원래 자리」고, 서버가
      // 보내는 st.p 가 현재 자리다 — 이걸 안 쓰면 다른 데 둔 물건을 영영 못 줍는다
      const px = st.p ? st.p[0] : def.pos[0];
      const pz = st.p ? st.p[2] : def.pos[2];
      const d = Math.hypot(px - player.pos.x, pz - player.pos.z);
      if (d > (def.reach ?? 2.4)) continue;
      // **벽 너머는 안 잡는다.** 예전에는 거리만 봐서 집 밖에서 안쪽 물건이 잡혔다.
      // 방이 같아야 한다. 문간에 걸친 물건 때문에 1.2m 안쪽은 봐준다.
      //
      // **문은 예외다.** 문은 방 경계에 서 있어서 밖에서 보면 언제나 방이 어긋난다.
      // 이 검사를 문에까지 걸었더니 바깥에서 어떤 문도 못 열게 됐다 —
      // 즉 건물에 들어갈 수가 없었다. 문은 원래 양쪽에서 쓰는 물건이다
      if (def.kind !== 'door') {
        // **선을 그어 본다.** 방 판정만 하고 1.2m 안쪽을 봐줬더니, 벽 두께가
        // 그보다 얇아서 **바깥 벽에 붙어 서면 안쪽 물건이 그대로 잡혔다.**
        // 문간은 벽 토막이 비어 있어 자연히 통과하고, 창은 유리가 막는다.
        if (wallBetween(player.pos.x, player.pos.z, px, pz)) continue;
        // 층이 다르면 못 잡는다. 언덕 위 집은 바닥이 3.6m 올라가 있어서
        // 평면으로만 보면 언덕 밑에서 집 안 물건이 사정거리에 든다.
        // **`def.pos[1]` 은 「바닥에서 얼마」(상대)다** — `v.floorY` 를 더해 절대
        // 높이로 맞춰야 한다(내려놓은 물건은 st.p 가 이미 절대 높이). 안 그러면
        // 언덕 위 바닥물건(pos[1]≈0)은 |3.6−0| 로 언제나 스킵된다 (2026-09-18)
        const py = st.p ? st.p[1] : (v.floorY ?? 0) + def.pos[1];
        if (Math.abs(player.pos.y - py) > 2.6) continue;
      }
      found = v;
      break;
    }

    if (found !== aimed) {
      aimed = found;
      highlight(props, found?.def.id ?? null);
      touchUI?.setAimed(found ? touchLabel(found) : null);
    }
    if (found) {
      hud.setPrompt(promptFor(found));
      return;
    }
    // 물건을 안 보고 있으면 **사람이 보인다.**
    // 남에 대해 알 수 있는 것은 여기까지다 — 왜 그런지는 그 사람 대본에 있다.
    hud.setPrompt(nearbyPerson() ?? dropHint());
  }

  /**
   * 들고 있는 것을 발밑에 두는 힌트 — **바닥을 보고 있을 때만** 뜬다.
   * 들고 다니는 내내 떠 있으면 「이 근처에 뭔가 있다」는 프롬프트의 신호가 죽는다
   * (2026-09-18, Chris: 「E 로 집고 E 로 바닥에 두고」)
   */
  function dropHint(): string | null {
    if (player.pitch > -0.45) return null;
    const mine = net.myItems();
    if (!mine.length) return null;
    const n = PROP_BY_ID[mine[0]]?.name ?? mine[0];
    return `<span class="k">[E]</span> ${n} 내려놓기`;
  }

  /** 앞에 있는 사람의 겉모습. 조사 횟수를 쓰지 않는다 — 보는 건 공짜다 */
  function nearbyPerson(): string | null {
    let best: { name: string; look: string; d: number } | null = null;
    const fx = -Math.sin(player.yaw);
    const fz = -Math.cos(player.yaw);
    for (const [id, p] of net.players) {
      if (id === net.you) continue;
      const buf = net.buffers.get(id);
      const last = buf?.[buf.length - 1];
      if (!last) continue;
      const dx = last.pose[1] - player.pos.x;   // PlayerPose = [id, x, y, z, yaw, pitch, flags]
      const dz = last.pose[3] - player.pos.z;
      const d = Math.hypot(dx, dz);
      if (d > 4.5 || d < 0.01) continue;
      if ((dx / d) * fx + (dz / d) * fz < 0.55) continue;   // 앞을 봐야 보인다
      const castId = net.world.roles[id];
      const look = lookOf(castId ?? null);
      if (!look) continue;
      const name = castId ? (CAST_BY_ID[castId]?.name ?? p.name) : p.name;
      if (!best || d < best.d) best = { name, look, d };
    }
    if (!best) return null;
    return `<b>${best.name}</b> — ${best.look.split('\n')[0]}`;
  }

  /** 터치 버튼에 들어갈 짧은 라벨 (동사 위주) */
  function touchLabel(v: PropView): string {
    const def = v.def;
    const st = net.obj(def.id);
    switch (def.kind) {
      case 'door':
        if (st.locked) return def.needs && net.myItems().includes(def.needs) ? '문 열기' : '잠김';
        return st.open ? '문 닫기' : '문 열기';
      case 'drawer':
        return st.open ? '서랍 닫기' : '서랍 열기';
      case 'lid':
        return def.dig
          ? `${def.name} ${st.open ? '다시 덮기' : '파기'}`
          : `${def.name} ${st.open ? '닫기' : '열기'}`;
      case 'switch':
        return st.on ? '불 끄기' : '불 켜기';
      case 'pickup':
        return `${def.name} 줍기`;
      case 'exam':
        return def.name;
      case 'safe':
        return st.solved ? '금고 보기' : '금고 열기';
      case 'board':
        return '증거 보드';
      default:
        return def.name;
    }
  }

  /** 지금 이 물건에 손을 못 대는 이유. 없으면 null */
  function blockWhy(def: PropView['def']): string | null {
    const investigable = def.kind === 'exam' || def.kind === 'safe' || def.kind === 'drawer' || def.kind === 'lid';
    if (!investigable && def.kind !== 'door') return null;
    const turnWhy = squadBlock(net.world.round, net.world.out, net.world.done, net.you ?? '');
    if (turnWhy) return turnWhy;
    if (investigable && !(def.clue && net.world.clues[def.clue])) {
      /* 단서 없는 lid·exam(내용물 노트)은 횟수를 안 쓴다 — 서버도 free 로 통과시킨다.
       * `awaiting` 서랍도 배달(stocked) 전엔 빈 통이라 같게 취급한다.
       * budget 을 비우면 investigateBlock 안에서 기본값이 들어가 항상 남는다 */
      const inert = !!def.awaiting && !net.world.objects[def.id]?.stocked;
      const free = (!def.clue || inert) && (def.kind === 'exam' || def.kind === 'lid');
      return investigateBlock(net.world.round, def, net.world.assigned[net.you], free ? undefined : net.world.budget[net.you]);
    }
    return null;
  }

  /** [R] 재생 가능 여부 — 조사가 끝난 기계만 (서버 onAct 의 play 분기와 같은 기준) */
  function playReady(def: PropView['def']): boolean {
    return !!(net.obj(def.id).solved || (def.clue && net.world.clues[def.clue]));
  }

  function promptFor(v: PropView): string {
    const def = v.def;
    const st = net.obj(def.id);
    const key = '<span class="k">[E]</span>';
    // 막혀 있으면 **누르기 전에** 이유를 보여준다
    const no = blockWhy(def);
    if (no) return `<span class="locked">${def.name} — ${no}</span>`;
    const lock = LOCKS[def.id];
    if (lock && !net.world.unlocked[def.id]) {
      return lock.kind === 'code'
        ? `${key} <span class="locked">${def.name} — 잠겨 있습니다 · 번호 ${lock.digits}자리</span>`
        : `${key} <span class="locked">${def.name} — 잠겨 있습니다</span>`;
    }
    switch (def.kind) {
      case 'door': {
        if (st.locked) {
          const need = def.needs ? PROP_BY_ID[def.needs]?.name : null;
          const have = def.needs && net.myItems().includes(def.needs);
          return have
            ? `${key} ${def.name} — ${need}로 열기`
            : `<span class="locked">${def.name} — ${need ?? '열쇠'}가 필요합니다</span>`;
        }
        return `${key} ${def.name} ${st.open ? '닫기' : '열기'}`;
      }
      case 'drawer':
        return `${key} ${def.name} ${st.open ? '닫기' : '열기'}`;
      case 'lid':
        // 파기는 F 키 — 줍기/놓기의 E 와 동사를 가른다 (2026-09-18, Chris)
        return def.dig
          ? `<span class="k">[F]</span> ${def.name} ${st.open ? '다시 덮기' : '파기'}`
          : `${key} ${def.name} ${st.open ? '닫기' : '열기'}`;
      case 'switch':
        return `${key} ${def.name} ${st.on ? '끄기' : '켜기'}`;
      case 'pickup':
        return `${key} ${def.name} 줍기`;
      case 'exam': {
        // 조사 끝난 재생 기계는 [R] 힌트를 붙인다 (v8 A11)
        const play = def.play && playReady(def) ? ' · R 재생' : '';
        return def.clue && net.world.clues[def.clue]
          ? net.world.shots?.[def.clue]
            ? `${key} ${def.name} 다시 보기${play}`
            : `${key} ${def.name} 다시 보기 · P 촬영${play}`
          : `${key} ${def.name} 조사하기${play}`;
      }
      case 'safe':
        return st.solved ? `${key} 금고 내용물 보기` : `${key} 금고 — 코드 입력`;
      case 'board':
        return `${key} 내 수첩 (${Object.keys(net.world.clues).length})`;
      default:
        return `${key} ${def.name}`;
    }
  }

  // --- 조명 컬링 ---
  // 방이 바뀌거나 문/스위치가 바뀔 때만 다시 계산한다 (매 프레임 할 일이 아니다)
  // 문/스위치 목록은 레벨에서 뽑는다. 예전에는 저택 id 가 박혀 있어서 다른
  // 레벨에서는 스위치를 꺼도 조명이 그대로 켜져 있었다.
  const DOOR_IDS = PROPS.filter((p) => p.kind === 'door').map((p) => p.id);
  /**
   * 포털(방) 컬링(portals.ts) — 보이는 방의 가구·소품만 그린다. `?portal=0` 이면 끈다. 조감도·편집기에서는 다 보인다
   */
  const portals = buildPortals({
    rooms: ROOMS, doorways: DOORWAYS, windows: WINDOWS, buildings: BUILDINGS, wallH: WALL_H,
    roomAt: (x, z) => epData().roomAt(x, z),
    doors: [...props.values()].filter((v) => v.def.kind === 'door').map((v) => {
      let glass = false;
      v.group.traverse((o) => { const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined; if (m && (Array.isArray(m) ? m : [m]).some((x) => x.transparent)) glass = true; });
      return { id: v.def.id, x: v.def.pos[0], z: v.def.pos[2], glass };
    }),
    isOpen: (id) => !!net.obj(id).open,
  });
  let portalOn = qs.get('portal') !== '0';
  let portalSig = '';
  const applyPortals = () => {
    const vis = portalOn && !overview.enabled && !editor.on ? portals.visible(camera) : null;
    const sig = vis ? [...vis].sort().join(',') : '*';
    if (sig === portalSig) return;
    portalSig = sig;
    for (const [id, box] of roomBoxes) box.visible = !vis || vis.has(id);
    built.setVisibleRooms(vis);
  };
  const SWITCH_ROOMS = PROPS.filter((p) => p.kind === 'switch' && p.lightRoom).map(
    (p) => [p.id, p.lightRoom!] as const,
  );
  let lightKey = '';
  function updateLightCulling() {
    const room = epData().roomAt(player.pos.x, player.pos.z)?.id ?? null;
    /* 건물별 그레이딩 — 방 id 앞머리로 건물을 가른다. 조합센터(cc_·cy_cafe·cy_roast)는 차갑게,
     * 관사(hs_·si_·yg_·mj_·cy_bed·bj_)는 따뜻하게, 작업동(mj_shop·yg_studio·yg_kiln)은 중간, 밖은 0 */
    pipeline.setWarmth(!room ? 0 : /^cc_|^cy_cafe|^cy_roast|^bj_fin/.test(room) ? -0.5 : /shop|studio|kiln|_dark|_photo/.test(room) ? 0.15 : 0.6);
    const open = new Set<string>();
    for (const id of DOOR_IDS) if (net.obj(id).open) open.add(id);
    const switches: Record<string, boolean> = {};
    for (const [sw, r] of SWITCH_ROOMS) switches[r] = net.obj(sw).on !== false;
    // **좌표 격자도 키에 넣는다.** 조명은 이제 고정 슬롯 풀이고 슬롯이 모자라면 가까운
    // 것이 이기므로, 방·문·스위치가 그대로여도 내가 4m 넘게 움직이면 다시 배정해야 한다.
    // 격자로 뭉개서 넣어야 매 프레임 재계산되지 않는다.
    const cell = `${Math.round(player.pos.x / 4)},${Math.round(player.pos.z / 4)}`;
    const key = `${overview.enabled}|${room}|${cell}|${[...open].sort().join(',')}|${JSON.stringify(switches)}`;
    if (key === lightKey) return;
    lightKey = key;
    built.cullLights(room, open, switches, overview.enabled, player.pos);
    // 켜진 조명이 바뀌면 그림자 맵이 통째로 달라진다
    shadowDirty = true;
  }
  net.on('world', updateLightCulling);
  net.on('world', () => touchUI?.setHolding(net.myItems().length > 0));

  // 맵 점검용. 배포 번들에는 안 들어간다
  if (import.meta.env.DEV || new URLSearchParams(location.search).has('dbg')) {
    (window as unknown as Record<string, unknown>).__propIds = PROPS.filter((x) => x.clue).map((x) => x.id);
    (window as unknown as Record<string, unknown>).__hiddenIn = Object.fromEntries(
      PROPS.filter((x) => x.clue && x.hiddenIn).map((x) => [x.id, x.hiddenIn]),
    );
    (window as unknown as Record<string, unknown>).__dbg = {
      /** 프롭 조작 — E 키와 같은 메시지. 촬영 스크립트가 덮개를 열어 보는 데 쓴다 */
      act: (id: string, a = 'use', arg?: string) => net.send({ t: 'act', id, a, arg }),
      /** 프롭 상태 — 검증 스크립트가 열림/애니메이션 진행을 본다 */
      objOf: (id: string) => { const v = props.get(id); const o = net.obj(id); return { open: !!o.open, on: o.on, target: v?.target ?? null, current: +(v?.current ?? 0).toFixed(2) }; },
      /** 시신 관절 포즈 — JSON 을 넣으면 바로 다시 눕는다 */
      pose(p: Record<string, Record<string, number>>) {
        const a = props.get('body')?.group.userData.avatar as { setPose: (p: unknown) => void } | undefined;
        a?.setPose(p);
      },
      /** 개리모드식 래그돌 포저 — 관절 핀·뼈 선을 띄우고 핀을 끌어 자세를 잡는다. 끄기: __dbg.ragdoll(false) */
      ragdoll(onOff = true) {
        const a = props.get('body')?.group.userData.avatar as import('./poser').PoserTarget | undefined;
        if (!a) return 'no body';
        if (!ragdollPoser) ragdollPoser = createRagdollPoser(scene, camera, renderer.domElement, a);
        if (!onOff) { ragdollPoser.disable(); return 'off'; }
        return ragdollPoser.enable() ? 'on' : 'model not ready';
      },
      /** 자동화용: 핀을 월드 좌표로 끈다 */
      ragdollDrag(joint: string, x: number, y: number, z: number) { return ragdollPoser?.dragTo(joint, x, y, z) ?? false; },
      /** 기즈모를 관절에 붙인다: __dbg.ragdollGizmo('LeftForeArm', 'rotate'|'translate'|'scale'). 키 1/2/3 으로 모드, Esc 로 뗀다 */
      ragdollGizmo(joint: string, mode: 'rotate' | 'translate' | 'scale' = 'rotate') { return ragdollPoser?.gizmo(joint, mode) ?? false; },
      ragdollGizmoTest(joint: string, mode: 'rotate' | 'translate' | 'scale', v: number[]) { return ragdollPoser?.gizmoTest(joint, mode, v) ?? false; },
      /** 시신에 UAL 클립을 입힌다: __dbg.deadClip('Death01', 1) */
      deadClip(name: string, at = 1) {
        const a = props.get('body')?.group.userData.avatar as { setDeadClip: (n: string, at?: number) => Promise<boolean> } | undefined;
        return a?.setDeadClip(name, at);
      },
      poseJSON() {
        const a = props.get('body')?.group.userData.avatar as { getPose: () => unknown } | undefined;
        return JSON.stringify(a?.getPose() ?? null);
      },
      /** 관절 슬라이더 패널. 값을 바꾸면 즉시 반영, 아래 텍스트가 JSON */
      async poser() {
        const { JOINTS } = await import('./motion');
        const a = props.get('body')?.group.userData.avatar as { setPose: (p: unknown) => void; getPose: () => Record<string, Record<string, number>> } | undefined;
        if (!a) return 'no body';
        const cur = JSON.parse(JSON.stringify(a.getPose()));
        const box = document.createElement('div');
        box.style.cssText = 'position:fixed;right:8px;top:60px;width:300px;max-height:80vh;overflow:auto;background:rgba(10,12,16,.92);color:#ddd;font:11px monospace;padding:8px;z-index:99;border:1px solid #444';
        const out = document.createElement('textarea');
        out.style.cssText = 'width:100%;height:80px;font:10px monospace;margin-top:6px';
        const push = () => { a.setPose(cur); out.value = JSON.stringify(cur); };
        for (const [j, spec] of Object.entries(JOINTS)) {
          const h = document.createElement('div'); h.textContent = j; h.style.cssText = 'margin-top:5px;color:#ffe0a3'; box.appendChild(h);
          for (const [p, q] of Object.entries(spec)) {
            const row = document.createElement('label'); row.style.cssText = 'display:flex;gap:6px;align-items:center';
            const s = document.createElement('input'); s.type = 'range'; s.min = String(q.min); s.max = String(q.max); s.step = '0.05';
            s.value = String(cur[j]?.[p] ?? 0); s.style.flex = '1';
            const v = document.createElement('span'); v.textContent = (+s.value).toFixed(2); v.style.width = '38px';
            s.oninput = () => { (cur[j] ??= {})[p] = +s.value; v.textContent = (+s.value).toFixed(2); push(); };
            row.append(p.padEnd(5), s, v); box.appendChild(row);
          }
        }
        box.appendChild(out); out.value = JSON.stringify(cur);
        document.body.appendChild(box);
        return 'poser on';
      },
      tp(x: number, z: number, yaw = 0, pitch?: number) {
        /**
         * **발밑을 다시 딛는다.** 예전에는 y 를 그대로 뒀는데, 언덕 위 평지가
         * 생기면서 한서진 집(3.6m)으로 옮기면 지형에 파묻혔다 — 촬영본이
         * 흙 속을 찍고 있었다.
         */
        player.pos.set(x, ground.topAt(x, z) ?? player.pos.y, z);
        player.yaw = yaw;
        if (pitch !== undefined) player.pitch = pitch;
      },
      where: () => ({ x: player.pos.x, z: player.pos.z, room: epData().roomAt(player.pos.x, player.pos.z)?.id ?? null }),
      /**
       * **걷게 한다.** 발자국 같은 「움직여야만 생기는 것」은 tp 로 검수할 수
       * 없고, 자동화에서 키보드로 누르고 있는 것은 창 포커스에 걸려 신뢰할 수
       * 없었다(6초를 눌러도 17cm 만 갔다). 터치 조이스틱과 **같은 입력 축**에
       * 값을 얹으므로 게임 코드 경로는 하나도 안 바뀐다. fwd 1 = 전진.
       */
      hold(fwd = 0, strafe = 0) {
        player.moveInput.y = fwd;
        player.moveInput.x = strafe;
      },
      /**
       * **정해진 거리를 걷는다 — 벽시계가 아니라 시뮬레이션 스텝으로.**
       *
       * 이 씬은 SwiftShader 소프트 렌더에서 초당 1프레임도 안 나온다(실측 0.1~0.7fps).
       * 로직 dt 는 0.05 로 클램프되므로 조이스틱을 벽시계 5초 동안 밀어도 실제로는
       * 네 프레임, 61cm 밖에 못 간다 — 「자동화에서 캐릭터가 안 움직인다」의 정체가
       * 이것이었다. 발자국처럼 **걸어야만 생기는 것**은 그래서 벽시계로 못 찍는다.
       *
       * 여기서는 렌더 루프와 **같은 순서**(player.update → footstep)를 고정 dt 로
       * 반복 호출한다. 충돌·지면 되돌림·발자국 스탬프 전부 실제 경로 그대로라서,
       * 벽에 막히면 여기서도 막힌다.
       */
      walk(meters: number, strafe = 0) {
        const D = 1 / 60;
        const n = Math.min(4000, Math.ceil((meters / 3.4) * 60) + 12);
        const x0 = player.pos.x, z0 = player.pos.z;
        const keep = { x: player.moveInput.x, y: player.moveInput.y };
        /**
         * **0.9 다.** 1 로 밀면 `stickMag > 0.92` 라 조이스틱 규칙상 **달리기**(5.6m/s)로
         * 판정돼서(`player.ts` 의 run 조건) 부른 거리의 1.65배를 가 버린다.
         * 실제로 walk(15) 가 26.85m 를 가서 집 안에 처박혔다.
         */
        player.moveInput.y = 0.9;
        player.moveInput.x = strafe;
        for (let i = 0; i < n; i++) {
          player.update(D);
          footstep('me', player.pos.x, player.pos.y, player.pos.z, player.yaw);
        }
        player.moveInput.x = keep.x;
        player.moveInput.y = keep.y;
        return {
          moved: +Math.hypot(player.pos.x - x0, player.pos.z - z0).toFixed(2),
          x: +player.pos.x.toFixed(2),
          y: +player.pos.y.toFixed(2),
          z: +player.pos.z.toFixed(2),
        };
      },
      phase: () => net.world.phase,
      /** 진단용 원본 참조 — 재질 uniform·레이어를 직접 들여다볼 때만 쓴다 */
      scene,
      camera,
      aimed: () => aimed?.def.id ?? null,
      /** 프롭 앞 1.4m 에 서서 정면으로 겨눈다. 조준되면 그 id 가 돌아온다 */
      probe(id: string) {
        const v = props.get(id);
        if (!v) return { ok: false, why: 'prop 없음' };
        const [px, , pz] = v.def.pos;
        // 방 중심 쪽에서 접근한다 — 밖에서 겨누면 벽 너머가 된다
        const r = epData().roomAt(px, pz);
        // 바깥 물건은 방 중심이 없다. 그럴 때는 남쪽에서 본다
        const cx = r ? (r.x1 + r.x2) / 2 : px;
        const cz = r ? (r.z1 + r.z2) / 2 : pz + 3;
        let dx = cx - px, dz = cz - pz;
        const len = Math.hypot(dx, dz) || 1;
        dx /= len; dz /= len;
        const dist = 1.4;
        player.pos.set(px + dx * dist, player.pos.y, pz + dz * dist);
        player.yaw = Math.atan2(dx, dz);
        // **위아래도 겨눈다.** 바닥에 있는 것(시신·혈흔·자국)은 수평으로 보면 안 맞는다
        const eye = camera.position.y || player.pos.y + 1.6;
        player.pitch = Math.atan2(v.def.pos[1] - eye, dist);
        return { ok: true, room: r?.id ?? null, pitch: player.pitch };
      },
      /**
       * **밤 마을은 그냥 검다.** 외관을 검수하려고 컷을 찍으면 건물이 통째로
       * 검은 덩어리로 나와 형태를 볼 수가 없다. 검수용으로만 반구광을 하나
       * 얹는다 — 게임 화면이 아니라 **도면을 보는 조명**이다.
       */
      flood(on = true) {
        const NAME = '__dbg_flood';
        const old = scene.getObjectByName(NAME);
        if (old) scene.remove(old);
        if (!on) return 'off';
        const g = new THREE.Group();
        g.name = NAME;
        g.add(new THREE.HemisphereLight(0xdfe8ff, 0x6a7080, 2.2));
        const d = new THREE.DirectionalLight(0xffffff, 1.6);
        d.position.set(30, 60, 40);
        g.add(d);
        const d2 = new THREE.DirectionalLight(0xffffff, 0.9);
        d2.position.set(-40, 30, -50);
        g.add(d2);
        scene.add(g);
        return 'on';
      },
      /**
       * **화면의 그 얼룩이 무엇인지 묻는다.** 컷을 보고 「저 황토색 판때기가
       * 뭐지」를 눈으로 추리하다 두 번 틀렸다. 화면 좌표(NDC, -1..1)로 광선을
       * 쏘아 맞은 메쉬의 이름·재질색·삼각형 수를 그대로 받는다.
       */
      /**
       * 위에서 **수직으로** 쏴서 그 자리의 지면 높이를 잰다.
       *
       * `pick` 은 화면 좌표라 「눈 둑이 몇 cm 냐」를 재려면 카메라를 맞춰 놓고
       * NDC 를 손으로 찍어야 한다 — 그림을 보고 값을 **추정**하게 되는 자리다.
       * 실제로 교문 앞 컷에서 둑이 「사람 키만 하다」고 적을 뻔했다. 월드 좌표로
       * 재면 추정할 일이 없다. y=8 에서 아래로 쏴 맨 위 교점을 돌려준다.
       */
      down(x: number, z: number) {
        const rc = new THREE.Raycaster();
        rc.set(new THREE.Vector3(x, 8, z), new THREE.Vector3(0, -1, 0));
        const h = rc.intersectObjects(scene.children, true)[0];
        if (!h) return null;
        const o = h.object as THREE.Mesh;
        const m = o.material as THREE.MeshStandardMaterial | undefined;
        return {
          y: +h.point.y.toFixed(3),
          n: o.name || '(무명)',
          parent: o.parent?.name || '(무명)',
          color: m && 'color' in m ? '#' + m.color.getHexString() : null,
          tris: o.geometry?.index ? o.geometry.index.count / 3 : 0,
        };
      },
      pick(nx: number, ny: number) {
        const rc = new THREE.Raycaster();
        rc.setFromCamera(new THREE.Vector2(nx, ny), camera);
        const hits = rc.intersectObjects(scene.children, true);
        return hits.slice(0, 4).map((h) => {
          const o = h.object as THREE.Mesh;
          const m = o.material as THREE.MeshStandardMaterial | undefined;
          const g = o.geometry as THREE.BufferGeometry | undefined;
          return {
            d: +h.distance.toFixed(2),
            p: [+h.point.x.toFixed(2), +h.point.y.toFixed(2), +h.point.z.toFixed(2)],
            n: o.name || '(무명)',
            parent: o.parent?.name || '(무명)',
            mat: Array.isArray(o.material) ? '(다중)' : (m?.name || '(무명)'),
            color: m && 'color' in m ? '#' + m.color.getHexString() : null,
            tris: g?.index ? g.index.count / 3 : (g?.attributes.position?.count ?? 0) / 3,
            hasMap: !!(m && m.map),
            /**
             * **크기와 조상 이름을 같이 받아야 정체를 안다.** 색과 삼각형 수만으로는
             * 「눈 색 12삼각형 상자」가 지붕 눈인지 굴뚝인지 문설주인지 못 가린다 —
             * 문틈 너머 흰 덩어리를 두 번 잘못 짚었다. 월드 bbox 를 보면 30cm 짜리
             * 소품인지 12m 짜리 지붕판인지 한눈에 갈린다.
             */
            type: o.type,
            box: (() => {
              const bb = new THREE.Box3().setFromObject(o);
              if (!isFinite(bb.min.x)) return null;
              const sz = bb.getSize(new THREE.Vector3());
              return {
                min: [+bb.min.x.toFixed(2), +bb.min.y.toFixed(2), +bb.min.z.toFixed(2)],
                size: [+sz.x.toFixed(2), +sz.y.toFixed(2), +sz.z.toFixed(2)],
              };
            })(),
            chain: (() => {
              const names: string[] = [];
              for (let q: THREE.Object3D | null = o; q; q = q.parent) if (q.name) names.push(q.name);
              return names.join(' < ') || null;
            })(),
          };
        });
      },
    };
  }

  // --- 루프 ---
  const clock = new THREE.Clock();
  let acc = 0;
  let frames = 0;
  let fps = 0;
  const reportInterval = 1000 / REPORT_HZ;

  /**
   * **프레임 상한 60** (2026-09-27, `?fps=0` 이면 풀고 `?fps=90` 처럼 바꾼다). 상한이 없어 맥북(120Hz)에서 초당 94~120장을
   * 그렸다 — GPU 가 쉬지 않아 몇 분 지나면 팬이 돌고 뜨거워졌다(Wayne). 추리 게임에 60 넘는 프레임은 보이지 않는다.
   * 모자란 프레임은 다음으로 넘기되 평균이 60 이 되게 누적한다(144Hz 에서도 48 로 떨어지지 않게)
   */
  const FPS_CAP = qs.get('fps') === '0' ? 0 : Number(qs.get('fps')) || 60;
  const capMs = FPS_CAP ? 1000 / FPS_CAP : 0;
  adaptive.setCap(capMs);
  let nextAt = 0;
  /**
   * **창에서 포커스가 빠지면 초당 20장.** 브라우저는 창이 보이기만 하면(다른 창을 보는 중이어도) 최대 속도로 그린다 —
   * 게임을 켜 둔 채 딴 일을 해도 GPU 가 돌았다. 네트워크·남의 움직임은 20장으로도 따라간다. `?fps=0` 이면 이것도 끈다
   */
  let focused = document.hasFocus();
  addEventListener('focus', () => { focused = true; });
  addEventListener('blur', () => { focused = false; });
  /**
   * **가만히 서 있으면 초당 30장** (2026-09-27). 추리 게임이라 서서 읽고 이야기하는 시간이 길다 — 카메라가 1초 넘게 그대로이고
   * 입력이 없으면 30으로 내린다. 입력이 오면 그 즉시(다음 화면 갱신에) 60으로 돌아온다. 눈이 내리는 건 30장이면 충분하다
   */
  let lastInput = performance.now();
  /** 지금 30장으로 쉬는 중 — 입력이 오면 다음 화면 갱신에 바로 그린다(쉬는 중일 때만. 늘 당기면 상한이 깨진다) */
  let resting = false;
  const onInput = () => { lastInput = performance.now(); if (resting) { resting = false; nextAt = 0; } };
  for (const ev of ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'touchmove']) addEventListener(ev, onInput, { passive: true });
  const lastCam = new Float64Array(16);
  let camStillSince = performance.now();

  function frame(now = performance.now()) {
    requestAnimationFrame(frame);
    if (capMs) {
      const idle = now - lastInput > 1000 && now - camStillSince > 1000;
      resting = idle;
      const gap = !focused ? Math.max(capMs, 50) : idle ? Math.max(capMs, 1000 / 30) : capMs;
      if (now < nextAt - 1) return;
      // 한 칸 넘게 뒤처졌으면(멈췄다 깨어남) 지금부터 다시 센다 — 몰아서 그리지 않는다
      nextAt = now - nextAt > gap ? now + gap : nextAt + gap;
    }
    {
      const e = camera.matrixWorld.elements;
      let same = true;
      for (let i = 0; i < 16; i++) if (e[i] !== lastCam[i]) { same = false; lastCam[i] = e[i]; }
      if (!same) camStillSince = now;
    }
    const rawDt = clock.getDelta();
    // 게임 로직은 튐 방지를 위해 클램프한다. 성능 판단에는 클램프하지 않은 실제 값을 쓴다.
    const dt = Math.min(0.05, rawDt);
    const t = clock.elapsedTime;
    renderer.info.reset();

    lobby.update();
    // 사건 쪽 애니메이션(초치성의 촛불 흔들림 등). 사흘은 빈 함수다 —
    // 예전에는 이 호출이 아예 없어서 촛불이 굳어 있었다
    built.update(t, camera.position);
    /**
     * 발 높이로 층을 정한다 — **그 층 충돌체만 켠다.**
     *
     * 안 부르면 모든 층의 벽이 동시에 살아 있어서, 지하 우리 벽이 지상 마당을
     * 막는다. 문이 열려 있는데 못 지나가던 원인이 이것이었다.
     */
    {
      const fs = data.level.floors;
      let best = fs[0];
      for (const f of fs) if (player.pos.y + 0.3 >= f.y && f.y >= best.y) best = f;
      if (best.id !== shownFloor) {
        shownFloor = best.id;
        built.setFloor(shownFloor);
      }
    }
    // 대기실/숙지 중에는 조작이 먹으면 안 된다. 뒤에서 아바타가 걸어 다닌다
    const staged = net.world.phase === 'lobby' || net.world.phase === 'read';
    if (!staged) {
      player.update(dt);
      footstep('me', player.pos.x, player.pos.y, player.pos.z, player.yaw);
    }
    built.ceiling.visible = !player.thirdPerson && !overview.enabled;
    // 지붕도 천장과 같다. 안 걷으면 부감이 지붕 등판 스무 장이 된다
    for (const r of facadeRoofs) r.visible = !overview.enabled;
    if (overview.enabled) {
      // player.update 가 방금 카메라를 1인칭으로 놨다. 그 위에 덮어쓴다
      overview.place(camera);
      overview.update(camera, window.innerWidth, window.innerHeight);
    }

    // 실내에서는 눈이 오면 안 된다. 눈은 카메라를 따라다니는 부피라
    // 지붕을 뚫고 방 안까지 내렸다.
    // **눈이 아예 없는 사건도 있다** — 초치성은 음력 보름의 가을밤이라
    // 마당에 눈이 내리면 이야기가 틀린다. 그때는 늘 꺼 둔다
    // **눈을 실내라고 통째로 끄지 않는다.** 그렇게 막았더니 방 안에서 창밖을
    // 보면 마을에 눈이 딱 그쳐 있었다. 실내에 눈이 안 들어오는 건 이제
    // 눈 셰이더의 지붕 마스크가 한다 (outside.ts).
    // 여기서 끄는 건 **눈이 아예 없는 사건** 뿐이다 — 초치성은 음력 보름의
    // 가을밤이라 마당에 눈이 내리면 이야기가 틀린다
    const noSnow = !data.snow;
    if (noSnow !== snowOff) {
      snowOff = noSnow;
      outside.setSnow(noSnow ? 0 : 1);
    }

    net.reportMove(player.pos.x, player.pos.y, player.pos.z, player.yaw, player.pitch, player.flags, reportInterval);

    if (!noRender) adaptive.update(rawDt);
    updateLightCulling();
    const remoteMoved = remotes.update(performance.now(), dt, camera.position);
    closeFarLids();
    const moved = animateProps(props, dt, t);
    built.curtains.update(dt, camera.position, built.root);
    if ((cullPieceAcc += dt) > 0.5) { cullPieceAcc = 0; built.cullPieces(player.pos, overview.enabled ? 999 : 24); }   // 먼 방 가구 안 그리기
    if ((decorAcc += dt) > 0.3) { decorAcc = 0; decor.update(camera.position); }   // 가까운 가로등·현관등을 슬롯에
    ragdollPoser?.update();
    outside.update(t, camera.position);
    updateAim();

    // 그림자를 다시 구울 이유가 있는가. 움직이는 문/서랍이 있거나, 가까이서 움직이는 남이 있거나
    // (원격 아바타는 castShadow 다), 3인칭으로 내 몸이 보이는 경우다. 혼자 가만히 서 있으면 한 번 구운
    // 그림자를 그대로 쓴다. 예전엔 누가 접속해 있기만 해도 늘 구웠다 — 대본을 읽느라 다들 서 있어도
    if (moved > 0 || remoteMoved > 0 || player.thirdPerson) shadowDirty = true;
    // 달빛 그림자 창(56m)이 플레이어를 따라간다. 4m 셀 단위로만 옮겨서 그림자
    // 텍셀이 매 프레임 흔들리지 않게 한다. 옮길 때만 다시 굽는다.
    if (moon) {
      const cx = Math.round(player.pos.x / 4) * 4, cz = Math.round(player.pos.z / 4) * 4;
      if (cx !== moonCell.x || cz !== moonCell.z) {
        moonCell.x = cx; moonCell.z = cz;
        moon.target.position.set(cx, 0, cz);
        moon.position.set(cx - 24, 26, cz - 20);
        shadowDirty = true;
      }
    }
    shadowAcc += dt;
    if (shadowDirty && shadowAcc >= SHADOW_DT) {
      requestShadowUpdate(renderer, scene);
      shadowAcc = 0;
      shadowDirty = false;
    }

    applyPortals();
    frozen?.update();
    bundles?.update(camera, player.pos);
    if (!noRender) pipeline.render(dt);

    frames++;
    dbg.frames++;
    // 나침반은 **매 프레임** 돌린다 — setDebug 는 0.5초마다라 뚝뚝 끊긴다.
    // transform 만 바꾸므로 레이아웃 비용이 없다
    hud.setCompass(player.yaw);
    editor.update();
    acc += dt;
    if (acc >= 0.5) {
      fps = Math.round(frames / acc);
      dbg.fps = fps;
      frames = 0;
      acc = 0;
      const r = epData().roomAt(player.pos.x, player.pos.z);
      const edLine = editor.aimedLabel();
      hud.setDebug([
        ...(edLine ? [`편집 \` · ${edLine}`] : []),
        `${fps} fps · ${net.rtt} ms`,
        `rev ${net.rev} · ${net.players.size}명`,
        `${r?.name ?? '마을 · 밖'} (${player.pos.x.toFixed(1)}, ${player.pos.z.toFixed(1)})`,
      ]);
      hud.setRound((() => {
          const rd = roundDef(net.world.round);
          const left = net.world.budget[net.you ?? ''] ?? rd.budget;
          const w = net.world;
          if (!w.out.length) {
            const rest = net.players.size - w.done.length;
            return `${rd.name} · 아무도 안 나감 · 남은 사람 ${rest}명 — O 로 짝을 정하세요`;
          }
          const sec = Math.max(0, Math.round((w.turnEndsAt - Date.now()) / 1000));
          const who = w.out.map((id) => net.players.get(id)?.name ?? '?').join('·');
          const mine = w.out.includes(net.you ?? '');
          return `${rd.name} · ${who} 나감 ${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}` +
            (mine ? ` · 조사 ${left}/${rd.budget}` : ' · 대기');
        })());
    }
  }
  frame();

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    // 창이 커지면 같은 픽셀 예산을 지키기 위해 비율이 내려가야 한다
    renderer.setPixelRatio(ratioFor(adaptive.current.budgetPx, maxRatio));
    renderer.setSize(window.innerWidth, window.innerHeight);
    pipeline.setSize(renderer.domElement.width, renderer.domElement.height);
    shadowDirty = true;
  });
}

function round(v: number) {
  return Math.round(v * 100) / 100;
}

function escapeAttr(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
